import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SuperToolResponseDto } from "@packages/api/madlenAPI.schemas";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "@core/index";

import LessonHooks from "../tools/lesson-hooks";
import PerformanceTask from "../tools/performance-task";
import CommonMisconception from "../tools/common-misconception";
import VocabularyList from "../tools/vocabulary-list";
import RealWorldConnections from "../tools/realworld-connections";
import ProjectBasedLearning from "../tools/project-based-learning";
import ExitTickets from "../tools/exit-tickets";
import FormativeAssessment from "../tools/formative-assessment";
import AiResistantHomework from "../tools/ai-resistant-homework";
import ProgressSteps from "../components/progress-steps";
import CollaborativeGroupWork from "../tools/collaborative-group-work";

type StepId = "start" | "build" | "connect" | "assess";

interface SuperToolViewProps {
  toolData: SuperToolResponseDto;
}

const SuperToolView: React.FC<SuperToolViewProps> = ({ toolData }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState<StepId>("start");

  const steps: StepId[] = ["start", "build", "connect", "assess"];

  useEffect(() => {
    const path = location.pathname;
    if (path.includes("/super-tool/")) {
      const stepFromPath = path.split("/super-tool/")[1] as StepId;
      if (["start", "build", "connect", "assess"].includes(stepFromPath)) {
        setActiveStep(stepFromPath);
      }
    }
  }, [location]);

  const handleStepClick = (stepId: StepId): void => {
    setActiveStep(stepId);
    navigate(`/super-tool/${stepId}`);
  };

  const handleNextStep = (): void => {
    const currentIndex = steps.indexOf(activeStep);
    if (currentIndex < steps.length - 1) {
      const nextStep = steps[currentIndex + 1];
      setActiveStep(nextStep);
      navigate(`/super-tool/${nextStep}`);
    }
  };

  const handlePreviousStep = (): void => {
    const currentIndex = steps.indexOf(activeStep);
    if (currentIndex > 0) {
      const prevStep = steps[currentIndex - 1];
      setActiveStep(prevStep);
      navigate(`/super-tool/${prevStep}`);
    }
  };

  const renderStepContent = (): JSX.Element => {
    switch (activeStep) {
      case "start":
        return (
          <div className="grid grid-cols-1 mt-8">
            <LessonHooks id={toolData.lessonHookId} />
          </div>
        );
      case "build":
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
            <div>
              <CommonMisconception id={toolData.commonMisconceptionId} />
            </div>
            <div>
              <VocabularyList id={toolData.vocabularyListId} />
            </div>
          </div>
        );
      case "connect":
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
            <div>
              <CollaborativeGroupWork id={toolData.collaborativeGroupWorkId} />
              <RealWorldConnections id={toolData.realWorldConnectionId} />
            </div>
            <div>
              <ProjectBasedLearning id={toolData.projectBasedLearningId} />
            </div>
          </div>
        );
      case "assess":
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
            <div>
              <PerformanceTask id={toolData.performanceTaskId} />
              <ExitTickets id={toolData.exitTicketGeneratorId} />
            </div>
            <div>
              <FormativeAssessment id={toolData.formativeAssessmentId} />
              <AiResistantHomework id={toolData.resistantHomeworkId} />
            </div>
          </div>
        );
      default:
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
            <div>
              <LessonHooks id={toolData.lessonHookId} />
              <CommonMisconception id={toolData.commonMisconceptionId} />
            </div>
            <div>
              <PerformanceTask id={toolData.performanceTaskId} />
              <VocabularyList id={toolData.vocabularyListId} />
            </div>
          </div>
        );
    }
  };

  const isFirstStep = activeStep === "start";
  const isLastStep = activeStep === "assess";

  return (
    <div className="container mx-auto p-4 max-w-7xl">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold mb-2">
            {t("superTool.platform.title")}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t("superTool.platform.description")}
          </p>
        </div>
      </div>

      <ProgressSteps
        steps={[
          {
            id: "start",
            title: t("superTool.steps.start"),
            isActive: activeStep === "start",
          },
          {
            id: "build",
            title: t("superTool.steps.build"),
            isActive: activeStep === "build",
          },
          {
            id: "connect",
            title: t("superTool.steps.connect"),
            isActive: activeStep === "connect",
          },
          {
            id: "assess",
            title: t("superTool.steps.assess"),
            isActive: activeStep === "assess",
          },
        ]}
        onStepClick={handleStepClick}
      />

      {renderStepContent()}

      <div className="mt-8 flex justify-between">
        <Button
          onClick={handlePreviousStep}
          bgColor="bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
          textColor="text-gray-800 dark:text-gray-200"
          size="md"
          rounded="lg"
          className={`flex items-center px-4 py-2 w-auto justify-center ${
            isFirstStep ? "invisible" : ""
          }`}
          data-testid="back-button"
          disabled={isFirstStep}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t("common.back")}
        </Button>
        <Button
          onClick={handleNextStep}
          bgColor="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
          textColor="text-white"
          size="md"
          rounded="lg"
          className={`flex items-center px-4 py-2 w-auto justify-center ${
            isLastStep ? "invisible" : ""
          }`}
          data-testid="next-button"
          disabled={isLastStep}
        >
          {t("common.next")}
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default SuperToolView;
